import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import backgroundimg from "./../image/banner.png";
import { Helmet } from "react-helmet";
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from "../firebase.config";

const Service = ({ title, showBanner = true }) => {
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(window.innerWidth < 768 ? 1 : 4);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const querySnapshot = await getDocs(collection(firestore, 'Service'));
        const fetchedServices = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setServices(fetchedServices);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
  }, []);

  const totalSlides = services.length;

  const handleResize = useCallback(() => {
    setVisibleSlides(window.innerWidth < 768 ? 1 : 4);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const updateSlideWidth = useCallback(() => {
    if (sliderRef.current && sliderRef.current.children.length > 0) {
      const slideElem = sliderRef.current.children[0];
      // Use getBoundingClientRect for more accurate width including borders and padding
      setSlideWidth(slideElem.getBoundingClientRect().width + (parseFloat(window.getComputedStyle(slideElem).marginRight) || 0));
    }
  }, [services]); // Update slideWidth when services load or resize

  useEffect(() => {
    updateSlideWidth();
    window.addEventListener("resize", updateSlideWidth);
    return () => window.removeEventListener("resize", updateSlideWidth);
  }, [updateSlideWidth]);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.transform = `translateX(-${currentIndex * slideWidth}px)`;
    }
  }, [currentIndex, slideWidth]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex + visibleSlides >= totalSlides ? 0 : prevIndex + 1
      );
    }, 3000);
    return () => clearInterval(interval);
  }, [totalSlides, visibleSlides]);
  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : totalSlides - 1));
  }, [totalSlides]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex < totalSlides - 1 ? prevIndex + 1 : 0));
  }, [totalSlides]);
  const visibleDots = Math.ceil(totalSlides / visibleSlides);
  const activeDotIndex = Math.floor(currentIndex / visibleSlides);
  const dots = Array.from({ length: visibleDots });

  const getDotIndex = useCallback((index) => {
    let result = index;
    if (Math.ceil(totalSlides / visibleSlides) > visibleDots) {
      if (activeDotIndex > Math.ceil(totalSlides / visibleSlides) - visibleDots) {
        result = Math.ceil(totalSlides / visibleSlides) - visibleDots + index;
      }
    }
    return result;
  }, [visibleSlides, totalSlides, activeDotIndex, visibleDots]);

  const handleLearnMoreClick = useCallback((serviceId) => {
    navigate(`/service-details/${serviceId}`);
  }, [navigate]);

  return (
    <div className="w-full h-auto bg-white lg:flex flex-col z-0 lg:mt-20 mt-0 lg:p-0 p-4">
      {showBanner && (
        <Helmet>
          <title>Our Services - Nexify InfoSystem</title>
          <meta
            name="description"
            content="Explore the wide range of services and solutions offered by Nexify InfoSystem to meet the unique needs of businesses."
          />
          <meta
            name="keywords"
            content="website development, digital marketing, mobile app development, cloud computing, cybersecurity solutions, IT consulting"
          />
        </Helmet>
      )}
      {showBanner && (
        <div
          className="w-full h-[440px] bg-cover bg-center relative flex items-center justify-center"
          style={{ backgroundImage: `url(${backgroundimg})` }}
        >
          <div className="relative z-0 flex justify-center lg:justify-start items-center w-full px-4 sm:px-10 lg:px-20">
            <div
              className="opacity-0 translate-y-10 animate-slideUp bg-[#0e55a8] p-6 shadow-lg text-white inline-block text-center lg:text-left max-w-[90%] sm:max-w-[80%] md:max-w-[60%] lg:max-w-[60%] lg:ml-14 border border-white/20 rounded-lg relative"
              style={{
                animation: "slideUp 1s ease-in-out forwards",
                animationDelay: "2s",
              }}
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.2)_1px,_transparent_1px)] bg-[size:6px_6px] opacity-50 pointer-events-none rounded-lg"></div>
              <h1 className="text-[8vw] md:text-[66px] font-semibold mb-2 break-words">
                Nexify Services
              </h1>
              <h2 className="text-[5vw] md:text-[40px] text-[#efefef]">
                Where Quality Meets Excellence
              </h2>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-[1320px] px-4 mx-auto mt-20 mb-5">
        <div className="relative z-0 items-center text-center lg:flex flex-col sm:block  justify-center">
          <h1 className="text-[30px] md:text-[40px] lg:text-[50px] text-black font-[700] leading-[1.5] mb-3">
            Innovative Technology <span className="text-[#005eb8]">Services and Advanced</span> Solutions Crafted for Your Unique Business Needs
          </h1>
          <p className="text-[16px] md:text-[18px] text-[#8d8d91] font-[500] leading-[1.5]">
            Nexify InfoSystem offers a wide range of services and solutions tailored to meet the unique needs of businesses.
          </p>
        </div>
        <div className="relative overflow-hidden mt-20">
          <button
            onClick={prevSlide}
            className="absolute lg:left-[8px] left-[0px] lg:top-[50%] top-[54%] transform -translate-y-1/2 bg-white shadow-md p-1 rounded-full z-10 hover:bg-gray-100 transition-colors duration-200"
            style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <div
            ref={sliderRef}
            className="flex transition-transform duration-500 ease-in-out"
            style={{ width: `${totalSlides * slideWidth}px` }}
          >
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white shadow-md p-6 flex flex-col items-start justify-start min-w-[300px] max-w-[300px] mr-4"
              >
                <div className="flex items-end justify-center text-[#005eb8] rounded-sm self-start">
                  <img src={service.imageurl} alt={service.title} className="w-36 h-36 object-contain" />
                </div>
                <h2 className="font-[700] text-[#0a0a0a] text-left text-[23px] h-[70px] overflow-hidden leading-tight">
                  {service.title}
                </h2>
                <div className="text-[#8d8d91] text-[16px] mb-2 h-[100px] overflow-hidden" dangerouslySetInnerHTML={{ __html: service.description }}>
                </div>
                <div className="self-start">
                  <button
                    onClick={() => handleLearnMoreClick(service.id)}
                    className="relative inline-block bg-[#f68b3e] text-white text-[12px] font-[500] p-[10px_16px] leading-[12px] rounded-[4px] transition-all duration-200 hover:border-none hover:text-white"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={nextSlide}
            className="absolute lg:right-[6px] lg:top-[50%] top-[54%] right-[0px] transform -translate-y-1/2 bg-white shadow-md p-1 rounded-full z-10 hover:bg-gray-100 transition-colors duration-200"
            style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
          <div className="lg:flex justify-center  hidden mt-4">
            {dots.map((_, index) => (
              <button
                key={index}
                className={`h-3 w-3 rounded-full mx-1 ${
                  activeDotIndex === getDotIndex(index) ? "bg-blue-500" : "bg-gray-300"
                }`}
                onClick={() => setCurrentIndex(getDotIndex(index) * visibleSlides)}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
export default Service